import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Button, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Fab
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  PlayArrow as PlayIcon, 
  Stop as StopIcon,
  Videocam as CameraIcon,
  Schedule as ScheduleIcon,
  SportsTennis as CourtIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { theme } from '../common/theme';

import { 
  fetchSiteOwnerCourts, 
  deleteCourtByCourtId, 
  startCourtStream, 
  stopCourtStream,
  setCurrentCourt,
  clearCurrentCourt,
  selectIsStreamActive
} from '../../store/CourtSlice';
import courtService from '../../services/courtService';
import CourtForm from './CourtForm';
import CourtStreamingPanel from './CourtStreamingPanel';
import CourtDetailsModal from './CourtDetailsModal';
import CourtHlsPlayer from './CourtHlsPlayer';

const CourtOwnerDashboard = () => {
  const dispatch = useDispatch();
  const { courts, loading, error, streaming } = useSelector((state) => state.court);
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingCourt, setEditingCourt] = useState(null);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [streamingData, setStreamingData] = useState({});
  const [courtOwners, setCourtOwners] = useState({});

  useEffect(() => {
    dispatch(fetchSiteOwnerCourts());
  }, [dispatch]);

  // Fetch owner names for each court
  useEffect(() => {
    const fetchOwners = async () => {
      const ownersMap = {};
      for (const court of courts) {
        try {
          const owners = await courtService.getCourtOwnerUsernames(court.courtId);
          ownersMap[court.courtId] = owners;
        } catch (error) {
          console.error(`Failed to fetch owners for court ${court.courtId}:`, error);
          ownersMap[court.courtId] = [];
        }
      }
      setCourtOwners(ownersMap);
    };

    if (courts.length > 0) {
      fetchOwners();
    }
  }, [courts]);

  const handleAddCourt = () => {
    setEditingCourt(null);
    setShowAddForm(true);
  };

  const handleEditCourt = (court) => {
    setEditingCourt(court);
    setShowEditForm(true);
  };

  const handleDeleteCourt = async (court) => {
    if (window.confirm(`Are you sure you want to delete "${court.courtName}"? This action cannot be undone.`)) {
      try {
        await dispatch(deleteCourtByCourtId(court.courtId)).unwrap();
        toast.success('Court deleted successfully');
      } catch (error) {
        toast.error(error.message || 'Failed to delete court');
      }
    }
  };

  const handleViewDetails = (court) => {
    setSelectedCourt(court);
    dispatch(setCurrentCourt(court));
    setShowDetailsModal(true);
  };

  const handleStartStream = async (court, rtspUrl) => {
    try {
      const streamRequest = {
        courtId: court.courtId,
        rtspUrl: rtspUrl
      };
      const result = await dispatch(startCourtStream(streamRequest)).unwrap();
      
      // Store the streaming data for this court
      setStreamingData(prev => ({
        ...prev,
        [court.courtId]: {
          hlsUrl: result.hlsUrl,
          streamKey: result.courtId
        }
      }));
      
      toast.success('Stream started successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to start stream');
    }
  };

  const handleStopStream = async (court) => {
    try {
      const stopRequest = {
        streamKey: court.courtId.toString()
      };
      await dispatch(stopCourtStream(stopRequest)).unwrap();
      
      // Clear the streaming data for this court
      setStreamingData(prev => {
        const newData = { ...prev };
        delete newData[court.courtId];
        return newData;
      });
      
      toast.success('Stream stopped successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to stop stream');
    }
  };

  const isStreamActive = (courtId) => {
    return streaming.activeStreams.includes(courtId);
  };

  const handleFormClose = () => {
    setShowAddForm(false);
    setShowEditForm(false);
    setEditingCourt(null);
  };

  const handleDetailsClose = () => {
    setShowDetailsModal(false);
    setSelectedCourt(null);
    dispatch(clearCurrentCourt());
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px' 
        }}
      >
        <CircularProgress 
          sx={{ 
            color: theme.colors.accentBlue,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            }
          }} 
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: theme.spacing.lg, maxWidth: '1400px', mx: 'auto' }}>
      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: theme.spacing.lg,
            backgroundColor: theme.colors.error + '10',
            border: `1px solid ${theme.colors.error}`,
            borderRadius: theme.borderRadius.md
          }}
        >
          {error}
        </Alert>
      )}

      {/* Streaming Error Alert */}
      {streaming.error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: theme.spacing.lg,
            backgroundColor: theme.colors.error + '10',
            border: `1px solid ${theme.colors.error}`,
            borderRadius: theme.borderRadius.md
          }}
        >
          Streaming Error: {streaming.error}
        </Alert>
      )}

      {/* Courts Grid */}
      <Grid container spacing={3}>
        {courts.length === 0 ? (
          <Grid item xs={12}>
            <Card 
              sx={{ 
                textAlign: 'center', 
                p: theme.spacing.xl,
                backgroundColor: theme.colors.secondaryBackground,
                borderRadius: theme.borderRadius.lg,
                boxShadow: theme.shadows.medium,
                border: `1px solid ${theme.colors.border}`,
                transition: theme.transitions.card,
                '&:hover': {
                  boxShadow: theme.shadows.large,
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent>
                <CourtIcon 
                  sx={{ 
                    fontSize: '4rem', 
                    color: theme.colors.textSecondary,
                    mb: theme.spacing.lg 
                  }} 
                />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: theme.spacing.md,
                    color: theme.colors.textPrimary,
                    fontFamily: theme.fonts.primary,
                    fontWeight: 'bold'
                  }}
                >
                  No Courts Found
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: theme.colors.textSecondary,
                    mb: theme.spacing.lg,
                    fontFamily: theme.fonts.secondary
                  }}
                >
                  Get started by adding your first court
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={handleAddCourt}
                  sx={{
                    backgroundColor: theme.colors.accentBlue,
                    color: theme.colors.white,
                    borderRadius: theme.borderRadius.md,
                    px: theme.spacing.lg,
                    py: theme.spacing.md,
                    fontFamily: theme.fonts.primary,
                    fontWeight: 'bold',
                    textTransform: 'none',
                    transition: theme.transitions.button,
                    '&:hover': {
                      backgroundColor: theme.colors.accentOrange,
                      transform: 'translateY(-1px)',
                      boxShadow: theme.shadows.medium
                    }
                  }}
                >
                  Add Your First Court
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          courts.map((court) => (
            <Grid item xs={12} sm={6} md={4} key={court.courtId}>
              <Card 
                sx={{ 
                  height: '100%',
                  backgroundColor: theme.colors.secondaryBackground,
                  borderRadius: theme.borderRadius.lg,
                  boxShadow: theme.shadows.medium,
                  border: `1px solid ${theme.colors.border}`,
                  transition: theme.transitions.card,
                  '&:hover': {
                    boxShadow: theme.shadows.large,
                    transform: 'translateY(-4px)',
                    borderColor: theme.colors.accentBlue
                  }
                }}
              >
                <CardContent sx={{ p: theme.spacing.lg }}>
                  {/* Court Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: theme.spacing.lg }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CourtIcon 
                        sx={{ 
                          color: theme.colors.accentBlue,
                          mr: theme.spacing.sm,
                          fontSize: '1.5rem'
                        }} 
                      />
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 'bold',
                          color: theme.colors.textPrimary,
                          fontFamily: theme.fonts.primary
                        }}
                      >
                        {court.courtName}
                      </Typography>
                    </Box>
                    <Chip 
                      label={isStreamActive(court.courtId) ? 'Live' : 'Offline'} 
                      color={isStreamActive(court.courtId) ? 'success' : 'default'}
                      size="small"
                      sx={{
                        backgroundColor: isStreamActive(court.courtId) ? theme.colors.success : theme.colors.textSecondary,
                        color: theme.colors.white,
                        fontWeight: 'bold',
                        fontFamily: theme.fonts.secondary
                      }}
                    />
                  </Box>

                  {/* Court Stats */}
                  <Box sx={{ mb: theme.spacing.lg, '& > *': { mb: theme.spacing.sm } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', color: theme.colors.textSecondary }}>
                      <CameraIcon sx={{ mr: theme.spacing.sm, fontSize: '1rem' }} />
                      <Typography variant="body2" sx={{ fontFamily: theme.fonts.secondary }}>
                        {court.cameras?.length || 0} Camera(s)
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', color: theme.colors.textSecondary }}>
                      <ScheduleIcon sx={{ mr: theme.spacing.sm, fontSize: '1rem' }} />
                      <Typography variant="body2" sx={{ fontFamily: theme.fonts.secondary }}>
                        {court.sessions?.length || 0} Session(s)
                      </Typography>
                    </Box>
                    {courtOwners[court.courtId] && courtOwners[court.courtId].length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: theme.colors.textSecondary }}>
                        <PersonIcon sx={{ mr: theme.spacing.sm, fontSize: '1rem' }} />
                        <Typography variant="body2" sx={{ fontFamily: theme.fonts.secondary }}>
                          Owner(s): {courtOwners[court.courtId].join(', ')}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Court Actions */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing.sm }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleViewDetails(court)}
                      sx={{
                        flex: 1,
                        borderColor: theme.colors.accentBlue,
                        color: theme.colors.accentBlue,
                        borderRadius: theme.borderRadius.md,
                        fontFamily: theme.fonts.secondary,
                        textTransform: 'none',
                        transition: theme.transitions.button,
                        '&:hover': {
                          backgroundColor: theme.colors.accentBlue,
                          color: theme.colors.white,
                          borderColor: theme.colors.accentBlue
                        }
                      }}
                    >
                      View Details
                    </Button>
                    
                    <Tooltip title="Edit Court">
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditCourt(court)}
                        sx={{
                          color: theme.colors.accentBlue,
                          transition: theme.transitions.button,
                          '&:hover': {
                            backgroundColor: theme.colors.accentBlue + '20',
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete Court">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteCourt(court)}
                        sx={{
                          color: theme.colors.error,
                          transition: theme.transitions.button,
                          '&:hover': {
                            backgroundColor: theme.colors.error + '20',
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Streaming Controls */}
                  {court.cameras && court.cameras.length > 0 && (
                    <Box sx={{ mt: theme.spacing.lg, pt: theme.spacing.lg, borderTop: `1px solid ${theme.colors.border}` }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: theme.colors.textSecondary,
                          mb: theme.spacing.sm,
                          display: 'block',
                          fontFamily: theme.fonts.secondary
                        }}
                      >
                        Quick Stream Actions:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: theme.spacing.xs }}>
                        {court.cameras.slice(0, 2).map((camera, index) => (
                          <Tooltip key={camera.id} title={`Stream ${camera.cameraName}`}>
                            <IconButton
                              size="small"
                              onClick={() => handleStartStream(court, camera.rtspUrl)}
                              disabled={streaming.loading}
                              sx={{
                                color: theme.colors.success,
                                transition: theme.transitions.button,
                                '&:hover': {
                                  backgroundColor: theme.colors.success + '20',
                                  transform: 'scale(1.1)'
                                },
                                '&:disabled': {
                                  color: theme.colors.textSecondary
                                }
                              }}
                            >
                              <PlayIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ))}
                        {isStreamActive(court.courtId) && (
                          <Tooltip title="Stop Stream">
                            <IconButton
                              size="small"
                              onClick={() => handleStopStream(court)}
                              disabled={streaming.loading}
                              sx={{
                                color: theme.colors.error,
                                transition: theme.transitions.button,
                                '&:hover': {
                                  backgroundColor: theme.colors.error + '20',
                                  transform: 'scale(1.1)'
                                },
                                '&:disabled': {
                                  color: theme.colors.textSecondary
                                }
                              }}
                            >
                              <StopIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Live Stream Preview */}
                  {isStreamActive(court.courtId) && streamingData[court.courtId]?.hlsUrl && (
                    <Box sx={{ mt: theme.spacing.lg, pt: theme.spacing.lg, borderTop: `1px solid ${theme.colors.border}` }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: theme.colors.textSecondary,
                          mb: theme.spacing.sm,
                          display: 'block',
                          fontFamily: theme.fonts.secondary
                        }}
                      >
                        Live Stream:
                      </Typography>
                      <Box 
                        sx={{ 
                          position: 'relative',
                          backgroundColor: theme.colors.darkBackground,
                          borderRadius: theme.borderRadius.md,
                          overflow: 'hidden',
                          height: '128px'
                        }}
                      >
                        <CourtHlsPlayer
                          hlsUrl={streamingData[court.courtId].hlsUrl}
                          autoPlay={true}
                          onError={(error) => {
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Floating Action Button */}
      <Fab
        sx={{
          position: 'fixed',
          bottom: theme.spacing.lg,
          right: theme.spacing.lg,
          backgroundColor: theme.colors.accentBlue,
          color: theme.colors.white,
          transition: theme.transitions.button,
          '&:hover': {
            backgroundColor: theme.colors.accentOrange,
            transform: 'scale(1.1)',
            boxShadow: theme.shadows.large
          }
        }}
        aria-label="add court"
        onClick={handleAddCourt}
      >
        <AddIcon />
      </Fab>

      {/* Court Form Dialog */}
      <Dialog 
        open={showAddForm || showEditForm} 
        onClose={handleFormClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.borderRadius.lg,
            backgroundColor: theme.colors.secondaryBackground,
            boxShadow: theme.shadows.large
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.colors.accentBlue,
            color: theme.colors.white,
            fontFamily: theme.fonts.primary,
            fontWeight: 'bold',
            fontSize: theme.fontSizes.lg
          }}
        >
          {showEditForm ? 'Edit Court' : 'Add New Court'}
        </DialogTitle>
        <DialogContent sx={{ p: theme.spacing.lg }}>
          <CourtForm
            court={editingCourt}
            onClose={handleFormClose}
            isEdit={showEditForm}
          />
        </DialogContent>
      </Dialog>

      {/* Court Details Modal */}
      <Dialog 
        open={showDetailsModal} 
        onClose={handleDetailsClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.borderRadius.lg,
            backgroundColor: theme.colors.secondaryBackground,
            boxShadow: theme.shadows.large
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.colors.accentBlue,
            color: theme.colors.white,
            fontFamily: theme.fonts.primary,
            fontWeight: 'bold',
            fontSize: theme.fontSizes.lg
          }}
        >
          Court Details: {selectedCourt?.courtName}
        </DialogTitle>
        <DialogContent sx={{ p: theme.spacing.lg }}>
          {selectedCourt && (
            <CourtDetailsModal
              court={selectedCourt}
              onClose={handleDetailsClose}
              onStartStream={handleStartStream}
              onStopStream={handleStopStream}
              isStreamActive={isStreamActive(selectedCourt.courtId)}
              streamingLoading={streaming.loading}
              streamingData={streamingData[selectedCourt.courtId]}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CourtOwnerDashboard;
